// Story 6.6 — real-user FPS monitor (pure decision logic).
//
// The production half of the 60-FPS reveal budget: while the ceremony
// plays we sample requestAnimationFrame deltas and decide, purely from
// the timing, whether the device is dropping frames badly enough that we
// should fall back to the mobile-fidelity tier (Story 3.7) for the rest
// of the session.
//
// This module is INTENTIONALLY pure — it knows nothing about rAF, the
// DOM, or React. It takes a stream of `performance.now()`-style
// timestamps via `pushFrame()` and returns the running verdict. The rAF
// plumbing lives in `useCeremonyFpsGuard`; the session switch lives in
// `sessionFidelityStore`. Keeping the decision pure is what lets the
// budget logic be unit-tested deterministically (DoD 2) instead of
// fighting a headless-timing harness.
//
// The default thresholds mirror the e2e budget so the runtime guard and
// the CI gate agree on what "janky" means:
//   * longFrameMs = 50  — the spec's per-frame jank ceiling. A frame
//     slower than this is the same "no individual frame > 50ms" line the
//     Playwright spec asserts.
//   * minAvgFps = 45    — a windowed floor well under the 58 CI target.
//     The guard is a SAFETY NET for real, sustained bad performance on a
//     weak device, not a re-run of the strict CI budget in the user's
//     browser; tripping degrade on a device that is merely a touch under
//     60 would over-degrade healthy sessions.

export interface FpsMonitorConfig {
  /** A frame whose delta exceeds this (ms) counts as a dropped/long
   *  frame. Default 50 — the e2e spec's per-frame jank ceiling. */
  longFrameMs: number;
  /** Number of leading frames ignored for the degrade decision — mount,
   *  first paint and layout warm-up are expected to be janky and must not
   *  trip the guard. They still count toward `frameCount`. Default 12. */
  warmupFrames: number;
  /** Rolling window (in frames) the average FPS is computed over. Default
   *  60 — roughly one second at 60 FPS. */
  windowFrames: number;
  /** Trip degrade once this many long frames are seen within the rolling
   *  window (after warm-up). Default 3 — a one-off hitch is tolerated; a
   *  cluster is not. */
  degradeAfterLongFrames: number;
  /** Trip degrade once the window is full AND its average FPS is below
   *  this floor (after warm-up). Default 45. */
  minAvgFps: number;
}

export const DEFAULT_FPS_MONITOR_CONFIG: FpsMonitorConfig = {
  longFrameMs: 50,
  warmupFrames: 12,
  windowFrames: 60,
  degradeAfterLongFrames: 3,
  minAvgFps: 45,
};

export interface FpsSample {
  /** Windowed average FPS (0 until at least two frames are seen). */
  fps: number;
  /** Long/dropped frames currently held in the rolling window. */
  longFrames: number;
  /** Total frames pushed so far (includes warm-up). */
  frameCount: number;
  /** Latched verdict: once true it stays true for this monitor. */
  degrade: boolean;
}

export interface FpsMonitor {
  /** Feed one frame timestamp (ms, monotonic like `performance.now()`).
   *  Returns the running verdict after this frame. */
  pushFrame(now: number): FpsSample;
  /** Current verdict without pushing a frame. */
  sample(): FpsSample;
  /** Reset all state (used by tests / replay). */
  reset(): void;
}

function computeAvgFps(deltas: number[]): number {
  if (deltas.length === 0) return 0;
  const sum = deltas.reduce((a, b) => a + b, 0);
  if (sum <= 0) return 0;
  return (deltas.length * 1000) / sum;
}

/** Create a pure FPS monitor. Pass a partial config to override any of
 *  the spec-aligned defaults. */
export function createFpsMonitor(
  config: Partial<FpsMonitorConfig> = {},
): FpsMonitor {
  const cfg: FpsMonitorConfig = { ...DEFAULT_FPS_MONITOR_CONFIG, ...config };

  let last: number | null = null;
  let frameCount = 0;
  let degraded = false;
  const deltas: number[] = []; // rolling window of the most recent deltas

  const sample = (): FpsSample => ({
    fps: computeAvgFps(deltas),
    longFrames: deltas.filter((d) => d > cfg.longFrameMs).length,
    frameCount,
    degrade: degraded,
  });

  return {
    pushFrame(now: number): FpsSample {
      frameCount += 1;

      if (last !== null) {
        const delta = now - last;
        // Guard against non-monotonic / duplicate timestamps, AND drop the
        // warm-up frames entirely: mount + first-paint jank must never
        // enter the window, or a slow first second would trip the guard
        // the instant the decision arms. Only steady-state frames count.
        if (delta > 0 && frameCount > cfg.warmupFrames) {
          deltas.push(delta);
          if (deltas.length > cfg.windowFrames) deltas.shift();
        }
      }
      last = now;

      // The degrade decision is only armed after the warm-up window.
      if (!degraded && frameCount > cfg.warmupFrames) {
        const longFrames = deltas.filter((d) => d > cfg.longFrameMs).length;
        const windowFull = deltas.length >= cfg.windowFrames;
        const avgFps = computeAvgFps(deltas);
        if (
          longFrames >= cfg.degradeAfterLongFrames ||
          (windowFull && avgFps < cfg.minAvgFps)
        ) {
          degraded = true;
        }
      }

      return sample();
    },
    sample,
    reset() {
      last = null;
      frameCount = 0;
      degraded = false;
      deltas.length = 0;
    },
  };
}
